import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Calendar, Package, DollarSign, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  pending_payment: "bg-yellow-100 text-yellow-800",
  confirmed: "bg-green-100 text-green-700",
  approved: "bg-blue-100 text-blue-700",
  completed: "bg-[#1A2F24] text-white",
  rejected: "bg-red-100 text-red-700",
  cancelled: "bg-gray-100 text-gray-600",
};

const STATUS_LABELS = {
  pending_payment: "Pending Payment",
  confirmed: "Confirmed",
  approved: "Approved",
  completed: "Completed",
  rejected: "Rejected",
  cancelled: "Cancelled",
};

export default function CustomerDashboard() {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBookings = async () => {
      try {
        const { data } = await axios.get(`${API}/bookings`, { withCredentials: true });
        setBookings(data);
      } catch (err) {
        console.error("Failed to fetch bookings:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchBookings();
  }, []);

  return (
    <div data-testid="customer-dashboard" className="min-h-screen bg-[#F9F8F6]">
      <div className="bg-[#1A2F24] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1
            className="text-3xl font-bold text-white tracking-tight"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            My Rentals
          </h1>
          <p className="text-white/60 text-sm mt-1">Welcome back, {user?.name}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-4 border-[#E68A00] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : bookings.length === 0 ? (
          <div className="text-center py-20">
            <Package className="w-12 h-12 text-[#E5E0D8] mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-[#1C1C1C] mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              No Rentals Yet
            </h3>
            <p className="text-sm text-[#595959] mb-4">Browse our equipment to get started.</p>
            <Link
              to="/equipment"
              data-testid="browse-equipment-link"
              className="inline-flex items-center bg-[#E68A00] hover:bg-[#CC7A00] text-white font-semibold px-6 py-2.5 rounded-md transition-colors text-sm"
            >
              Browse Equipment
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {bookings.map((booking) => (
              <div
                key={booking.id}
                data-testid={`booking-card-${booking.id}`}
                className="bg-white border border-[#E5E0D8] rounded-lg p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-semibold text-[#1C1C1C]" style={{ fontFamily: "'Outfit', sans-serif" }}>
                      {booking.equipment_name}
                    </h3>
                    <Badge className={STATUS_STYLES[booking.status] || "bg-gray-100 text-gray-600"}>
                      {STATUS_LABELS[booking.status] || booking.status}
                    </Badge>
                  </div>
                  <div className="flex flex-wrap gap-4 text-sm text-[#595959]">
                    <span className="flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5" />
                      {booking.start_date} to {booking.end_date}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5" />
                      {booking.rental_type}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <DollarSign className="w-3.5 h-3.5" />
                      ${booking.total?.toFixed(2)}
                    </span>
                  </div>
                </div>
                <div>
                  <Badge className={booking.payment_status === "paid" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-800"}>
                    {booking.payment_status === "paid" ? "Paid" : "Unpaid"}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
